/**
 * Keplerian orbital mechanics helper.
 *
 * Computes a 3D position on an elliptical orbit from standard orbital elements.
 * Returns [x, y, z] in Three.js world space where:
 *   • The ecliptic plane  → Three.js XZ plane  (y ≈ 0 for orbits near ecliptic)
 *   • Ecliptic north pole → Three.js +Y  (up)
 *
 * Orbital elements:
 *   a     – semi-major axis (game units)
 *   e     – eccentricity  (0 = circle, 0–1 = ellipse)
 *   inc   – inclination in degrees  (tilt of orbit plane vs. ecliptic)
 *   omega – argument of perihelion in degrees  (orientation within orbit plane)
 *   Omega – longitude of ascending node in degrees  (orbit plane twist)
 *   nu    – eccentric anomaly parameter in radians  (0 = perihelion direction)
 *
 * Derivation: standard rotation  Rz(-Ω) · Rx(-i) · Rz(-ω)  applied to the
 * parametric ellipse (a·cos ν, b·sin ν) in the perifocal frame.
 */
export function getOrbitPosition(a, e, inc, omega, Omega, nu) {
  const b  = a * Math.sqrt(1 - e * e);       // semi-minor axis
  const I  = (inc   * Math.PI) / 180;
  const W  = (omega * Math.PI) / 180;
  const O  = (Omega * Math.PI) / 180;

  // Parametric position in perifocal (orbital) frame
  const xp = a * Math.cos(nu);
  const yp = b * Math.sin(nu);

  // 1 — rotate by argument of perihelion ω within the orbital plane
  const cW = Math.cos(W), sW = Math.sin(W);
  const x1 =  cW * xp - sW * yp;
  const y1 =  sW * xp + cW * yp;

  // 2 — apply inclination (tilt around line of nodes)
  const cI = Math.cos(I), sI = Math.sin(I);
  const x2 = x1;
  const y2 = y1 * cI;
  const z2 = y1 * sI;   // ecliptic-north component

  // 3 — rotate by ascending node Ω around ecliptic north
  const cO = Math.cos(O), sO = Math.sin(O);
  const x3 =  cO * x2 - sO * y2;
  const y3 =  sO * x2 + cO * y2;
  const z3 = z2;

  // Map ecliptic (X, Y_ecl, Z_north) → Three.js (X, Y_up=Z_north, Z=Y_ecl)
  return [x3, z3, y3];
}
